import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './CookieConsent.css';

const COOKIE_CONSENT_KEY = 'canira_cookie_consent';

/**
 * Cookie consent — a small card in the bottom-left corner.
 *
 * Kept small on purpose: it used to be a full-width bar (173px on a desktop,
 * 304px — over a third of the screen — on a phone) in off-brand blue, and the
 * homepage training card waits for it to be answered. Bottom-LEFT because the
 * training card and the help launcher both live bottom-right.
 *
 * "Essential only" and "Accept all" are the same size, side by side: refusing
 * must be as easy as accepting. The saved shape, the storage key and the
 * `cookieConsentChanged` event are unchanged — analytics and the pixels read
 * them (`getCookieConsent`, `useCookieConsent`).
 */
const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Always required
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    // Check if user has already made a choice
    const savedConsent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!savedConsent) {
      // Delay showing banner slightly for better UX
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    } else {
      // Load saved preferences
      try {
        const parsed = JSON.parse(savedConsent);
        setPreferences(parsed);
      } catch {
        // Invalid data, show banner again
        setShowBanner(true);
      }
    }
  }, []);

  const saveConsent = (prefs) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(prefs));
    setPreferences(prefs);
    setShowBanner(false);
    setShowPreferences(false);

    // Dispatch event for analytics tools to listen to
    window.dispatchEvent(new CustomEvent('cookieConsentChanged', { detail: prefs }));
  };

  const acceptAll = () => {
    saveConsent({
      essential: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString(),
    });
  };

  const acceptEssentialOnly = () => {
    saveConsent({
      essential: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString(),
    });
  };

  const savePreferences = () => {
    saveConsent({
      ...preferences,
      timestamp: new Date().toISOString(),
    });
  };

  if (!showBanner) return null;

  const toggle = (key) => setPreferences((p) => ({ ...p, [key]: !p[key] }));

  return (
    <div
      className={`ckc-card${showPreferences ? ' ckc-card--settings' : ''}`}
      role="region"
      aria-label="Cookie consent"
    >
      {!showPreferences ? (
        <>
          <p className="ckc-text">
            We use essential cookies to run the site. With your OK, we also use cookies to
            measure visits and improve our ads. <Link to="/privacy">Privacy Policy</Link>
          </p>
          {/* Refusing is exactly as easy as accepting: same size, side by side. */}
          <div className="ckc-actions">
            <button type="button" className="ckc-btn ckc-btn--plain" onClick={acceptEssentialOnly}>
              Essential only
            </button>
            <button type="button" className="ckc-btn ckc-btn--accept" onClick={acceptAll}>
              Accept all
            </button>
          </div>
          <button type="button" className="ckc-link" onClick={() => setShowPreferences(true)}>
            Cookie settings
          </button>
        </>
      ) : (
        <>
          <p className="ckc-title">Cookie settings</p>
          <ul className="ckc-prefs">
            <li className="ckc-pref">
              <span className="ckc-pref-text">
                <span className="ckc-pref-name">Essential</span>
                <span className="ckc-pref-desc">Needed for the site to work. Always on.</span>
              </span>
              <input
                type="checkbox"
                className="ckc-switch"
                checked
                disabled
                aria-label="Essential cookies (always on)"
              />
            </li>
            <li className="ckc-pref">
              <span className="ckc-pref-text">
                <span className="ckc-pref-name">Analytics</span>
                <span className="ckc-pref-desc">Helps us see how visitors use the site.</span>
              </span>
              <input
                type="checkbox"
                className="ckc-switch"
                checked={!!preferences.analytics}
                onChange={() => toggle('analytics')}
                aria-label="Analytics cookies"
              />
            </li>
            <li className="ckc-pref">
              <span className="ckc-pref-text">
                <span className="ckc-pref-name">Marketing</span>
                <span className="ckc-pref-desc">Helps us measure and improve our ads.</span>
              </span>
              <input
                type="checkbox"
                className="ckc-switch"
                checked={!!preferences.marketing}
                onChange={() => toggle('marketing')}
                aria-label="Marketing cookies"
              />
            </li>
          </ul>
          <div className="ckc-actions">
            <button type="button" className="ckc-btn ckc-btn--plain" onClick={() => setShowPreferences(false)}>
              Back
            </button>
            <button type="button" className="ckc-btn ckc-btn--accept" onClick={savePreferences}>
              Save
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default CookieConsent;

/**
 * Helper hook to check cookie consent status
 */
export const useCookieConsent = () => {
  const [consent, setConsent] = useState(null);

  useEffect(() => {
    const loadConsent = () => {
      const saved = localStorage.getItem(COOKIE_CONSENT_KEY);
      if (saved) {
        try {
          setConsent(JSON.parse(saved));
        } catch {
          setConsent(null);
        }
      }
    };

    loadConsent();

    // Listen for consent changes
    const handleChange = (e) => setConsent(e.detail);
    window.addEventListener('cookieConsentChanged', handleChange);
    return () => window.removeEventListener('cookieConsentChanged', handleChange);
  }, []);

  return {
    hasConsent: consent !== null,
    analyticsAllowed: consent?.analytics === true,
    marketingAllowed: consent?.marketing === true,
  };
};

/**
 * Non-hook reader for the stored consent choice, for non-React modules
 * (analytics/pixel bootstrap) that gate on it. Returns the saved
 * { essential, analytics, marketing } object, or null if no choice yet.
 */
export const getCookieConsent = () => {
  try {
    const saved = localStorage.getItem(COOKIE_CONSENT_KEY);
    return saved ? JSON.parse(saved) : null;
  } catch {
    return null;
  }
};
